import { useEffect, useState } from "react";
import { api, eur, frDate, openPdf } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileDown } from "lucide-react";

const empty = {
  reference: "", donor_name: "", donor_address: "", donor_zip: "", donor_city: "",
  amount: 0, amount_words: "",
  donation_date: new Date().toISOString().slice(0, 10),
  issue_date: new Date().toISOString().slice(0, 10),
  payment_method: "Virement",
};

export default function RecusDon() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/donation-receipts").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.donor_name || !form.amount) { toast.error("Donateur et montant requis"); return; }
    try {
      const payload = { ...form, amount: Number(form.amount) || 0,
        reference: form.reference || `DON-${Date.now().toString().slice(-6)}` };
      if (edit) await api.put(`/donation-receipts/${edit.id}`, payload);
      else await api.post("/donation-receipts", payload);
      toast.success(edit ? "Reçu modifié." : "Reçu de don créé.");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const remove = async (r) => {
    if (!window.confirm("Supprimer ce reçu ?")) return;
    await api.delete(`/donation-receipts/${r.id}`); load();
  };

  const total = rows.reduce((a, b) => a + Number(b.amount || 0), 0);

  return (
    <div data-testid="recus-don-page">
      <PageHeader
        eyebrow="Fiscalité"
        title="Reçus de don"
        description="Émettez les reçus ouvrant droit à la réduction d'impôt (article 200 du CGI) pour vos donateurs."
        actions={
          <Button data-testid="new-receipt-btn" onClick={() => { setEdit(null); setForm({ ...empty, reference: `DON-${Date.now().toString().slice(-6)}` }); setOpen(true); }} className="bg-brand-orange hover:bg-[#D04A06] text-white">
            <Plus size={16} className="mr-1" /> Nouveau reçu
          </Button>
        }
      />

      <div className="bg-white border border-brand-border rounded-xl p-5 mb-6">
        <div className="small-caps">Total des dons enregistrés</div>
        <div className="font-serif text-3xl text-brand-green mt-1">{eur(total)}</div>
      </div>

      <div className="bg-white border border-brand-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 small-caps">
            <tr>
              <th className="text-left py-3 px-4">Référence</th>
              <th className="text-left py-3 px-4">Donateur</th>
              <th className="text-left py-3 px-4">Date du don</th>
              <th className="text-left py-3 px-4">Mode</th>
              <th className="text-right py-3 px-4">Montant</th>
              <th className="text-right py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-gray-400">Aucun reçu.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-brand-border hover:bg-gray-50">
                <td className="py-3 px-4 font-mono text-xs">{r.reference}</td>
                <td className="py-3 px-4 font-medium text-gray-900">{r.donor_name}<div className="text-xs text-gray-400">{r.donor_city}</div></td>
                <td className="py-3 px-4 text-gray-700">{frDate(r.donation_date)}</td>
                <td className="py-3 px-4 text-gray-700">{r.payment_method}</td>
                <td className="py-3 px-4 text-right font-semibold text-brand-green">{eur(r.amount)}</td>
                <td className="py-3 px-4 text-right space-x-1">
                  <Button size="sm" variant="outline" onClick={() => openPdf(`/donation-receipts/${r.id}/pdf`)}><FileDown size={14} className="mr-1" />PDF</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEdit(r); setForm({...empty, ...r}); setOpen(true); }}><Pencil size={14} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(r)}><Trash2 size={14} className="text-red-500" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-brand-navy">{edit ? "Modifier le reçu" : "Nouveau reçu de don"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div><Label>Référence</Label><Input value={form.reference} onChange={(e) => setForm({...form, reference: e.target.value})} /></div>
            <div><Label>Date d'émission</Label><Input type="date" value={form.issue_date} onChange={(e) => setForm({...form, issue_date: e.target.value})} /></div>
            <div className="md:col-span-2"><Label>Nom du donateur *</Label><Input value={form.donor_name} onChange={(e) => setForm({...form, donor_name: e.target.value})} /></div>
            <div className="md:col-span-2"><Label>Adresse</Label><Input value={form.donor_address} onChange={(e) => setForm({...form, donor_address: e.target.value})} /></div>
            <div><Label>Code postal</Label><Input value={form.donor_zip} onChange={(e) => setForm({...form, donor_zip: e.target.value})} /></div>
            <div><Label>Ville</Label><Input value={form.donor_city} onChange={(e) => setForm({...form, donor_city: e.target.value})} /></div>
            <div><Label>Montant (€) *</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} /></div>
            <div><Label>Date du don</Label><Input type="date" value={form.donation_date} onChange={(e) => setForm({...form, donation_date: e.target.value})} /></div>
            <div className="md:col-span-2"><Label>En toutes lettres</Label><Input value={form.amount_words} onChange={(e) => setForm({...form, amount_words: e.target.value})} placeholder="Cinquante euros" /></div>
            <div><Label>Mode de paiement</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({...form, payment_method: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Virement">Virement</SelectItem>
                  <SelectItem value="Chèque">Chèque</SelectItem>
                  <SelectItem value="Espèces">Espèces</SelectItem>
                  <SelectItem value="Carte bancaire">Carte bancaire</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} className="bg-brand-orange hover:bg-[#D04A06] text-white">{edit ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
